/**
 * `<schmancy-form-summary>` — top-of-form error summary with anchor links to
 * each invalid field. Linear / GOV.UK / GitHub pattern.
 *
 * Drop one inside any `<schmancy-form>`; when a submit is refused by the form's
 * own validation, the summary renders a `role="alert"` banner with the
 * form-level error message and a list of links pointing at each invalid field's
 * `id`.
 *
 * NOT on `setFormError(...)`. A refusal is announced by ONE surface, and the
 * form routes a server reject to its own `<schmancy-alert>` instead
 * (`form.ts`'s `broadcastStatus` defaults to the `alert` surface; only the
 * validation path asks for `summary`). Rendering here as well would say one
 * sentence twice, in two `role="alert"` boxes. Whether mounting this component
 * should claim the surface and stand that alert down is an open question, not a
 * thing this component decides on its own.
 *
 * Hidden when the form is idle, submitting, or successful — only `error`
 * populates it.
 *
 * @element schmancy-form-summary
 */

import { html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SchmancyElement } from '@schmancy/mixins'
import { bringFieldIntoView } from './bring-field-into-view'
import type SchmancyForm from './form'

@customElement('schmancy-form-summary')
export class SchmancyFormSummary extends SchmancyElement {
	/**
	 * Optional heading text. Defaults to a count-based summary
	 * ("1 problem with this form" / "3 problems with this form").
	 */
	@property({ type: String }) heading?: string

	/**
	 * Render bumper — incremented to force a re-render when the form's state
	 * changes (the actual content is computed from the DOM in render()).
	 */
	@state() private bump: number = 0

	private form: SchmancyForm | null = null
	private formObserver?: MutationObserver

	override connectedCallback(): void {
		super.connectedCallback()
		this.form = this.closest('schmancy-form') as SchmancyForm | null
		if (!this.form) {
			console.error('[schmancy-form-summary] must be a descendant of <schmancy-form>')
			return
		}
		const bump = () => {
			this.bump++
		}
		this.formObserver = new MutationObserver(bump)
		// Form host attribute changes (aria-busy, :state class reflections,
		// descendant attribute changes for fields' aria-invalid). subtree:true
		// catches attribute mutations on every descendant including light-DOM
		// fields and the form's own host.
		this.formObserver.observe(this.form, {
			attributes: true,
			subtree: true,
		})
		// Defer live-region observation until the form has rendered its
		// shadow tree.
		const attachLiveRegionObserver = async (): Promise<void> => {
			await this.form?.updateComplete
			const liveRegion = this.form?.shadowRoot?.querySelector('[role="status"]')
			if (liveRegion) {
				this.formObserver?.observe(liveRegion, {
					childList: true,
					characterData: true,
					subtree: true,
				})
			}
			// Force a render so the initial state is reflected.
			this.bump++
		}
		void attachLiveRegionObserver()
	}

	override disconnectedCallback(): void {
		this.formObserver?.disconnect()
		this.formObserver = undefined
		super.disconnectedCallback()
	}

	private readFormStatus(): 'idle' | 'submitting' | 'success' | 'error' {
		if (!this.form) return 'idle'
		if (this.form.matches(':state(error)')) return 'error'
		if (this.form.matches(':state(submitting)')) return 'submitting'
		if (this.form.matches(':state(success)')) return 'success'
		return 'idle'
	}

	private readFormMessage(): string {
		const liveRegion = this.form?.shadowRoot?.querySelector('[role="status"]')
		return liveRegion?.textContent?.trim() ?? ''
	}

	private readInvalidFields(): Array<{ id: string; label: string; message: string }> {
		if (!this.form) return []
		const result: Array<{ id: string; label: string; message: string }> = []
		for (const el of Array.from(this.form.querySelectorAll('*'))) {
			if (!(el instanceof HTMLElement)) continue
			let isInvalid = false
			try {
				isInvalid = el.matches(':state(invalid)')
			} catch {
				continue
			}
			if (!isInvalid) continue
			const f = el as HTMLElement & {
				label?: string
				name?: string
				validationMessage?: string
			}
			result.push({
				id: f.id,
				label: f.label || f.name || '(field)',
				message: f.validationMessage || 'Invalid value',
			})
		}
		return result
	}

	render() {
		// Read everything fresh from the DOM each render — no @state cache, no
		// re-render loops. The MutationObserver bumps `bump` to trigger this
		// render; `bump` itself is never read here.
		void this.bump

		const status = this.readFormStatus()
		if (status !== 'error') return nothing

		const formMessage = this.readFormMessage()
		const invalid = this.readInvalidFields()
		const count = invalid.length
		// The <p> carries what the per-field list cannot say — a server reject, a
		// network failure, a rule spanning several fields. When exactly one field
		// blocks the submit, the form announces that field's OWN sentence, so the
		// same words would land here and again in the list below, the second time
		// naming the field they belong to and linking to it. Print them once,
		// where they are attributed.
		const formMessageSaysSomethingNew = !!formMessage && !(count === 1 && formMessage === invalid[0].message)
		const heading =
			this.heading ??
			(count === 0
				? 'There is a problem with this form'
				: `There ${count === 1 ? 'is' : 'are'} ${count} problem${count === 1 ? '' : 's'} with this form`)

		return html`
			<div role="alert" aria-labelledby="schmancy-form-summary-heading">
				<h2 id="schmancy-form-summary-heading">${heading}</h2>
				${formMessageSaysSomethingNew ? html`<p>${formMessage}</p>` : nothing}
				${
					count > 0
						? html`
								<ul>
									${invalid.map(
									f => html`
										<li>
											<a href="#${f.id}" @click=${(e: MouseEvent) => this.jumpToField(e, f.id)}
												>${f.label}: ${f.message}</a
											>
										</li>
									`,
								)}
								</ul>
							`
						: nothing
				}
			</div>
		`
	}

	private jumpToField(e: MouseEvent, fieldId: string): void {
		e.preventDefault()
		// preventDefault() cancels the browser's own fragment-navigation scroll,
		// which is the only thing that was moving the region — `focus()`'s
		// implicit one lands `nearest` and is satisfied the moment the field is
		// anywhere on screen. This link is the escape hatch for an operator who
		// cannot see the field; it has to actually go there.
		bringFieldIntoView(this.form?.querySelector<HTMLElement>(`#${CSS.escape(fieldId)}`))
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-form-summary': SchmancyFormSummary
	}
}
